import { useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { X, Loader2, AlertTriangle, CheckCircle2 } from 'lucide-react'
import { fetchSyncReportDetail } from '../../../slice/sharepointSlice'

const FILE_TYPE_LABELS = {
  pdf: 'PDF', docx: 'Word (.docx)', doc: 'Word (.doc)',
  xlsx: 'Excel (.xlsx)', xls: 'Excel (.xls)', csv: 'CSV',
  pptx: 'PowerPoint (.pptx)', txt: 'Text (.txt)',
}

function formatFileTypes(fileTypesStr) {
  if (!fileTypesStr) return '—'
  return fileTypesStr.split(',').filter(Boolean)
    .map(ext => FILE_TYPE_LABELS[ext.toLowerCase()] || ext.toUpperCase())
    .join(', ')
}

// The backend sends a stable code (not a sentence) for known, translatable
// failure reasons — a real exception's message can't be translated, so it's
// shown as-is, same as before.
function formatEmbedError(error, t) {
  if (error === 'no_text_extracted_scanned_pdf') return t('agentSettings.embedErrorNoTextExtracted')
  return error
}

function formatDateTime(iso) {
  if (!iso) return '—'
  const d = new Date(iso)
  return `${d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}, ${d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}`
}

function formatDuration(started, finished) {
  if (!started || !finished) return '—'
  const s = Math.round((new Date(finished) - new Date(started)) / 1000)
  if (s < 60) return `${s}s`
  return `${Math.floor(s / 60)}m ${s % 60}s`
}

// Shared by "View Import Report" and "View Deletion Report" — a deletion
// writes the exact same SharePointSyncReports shape (report_type='delete'
// instead of 'import'), so this one modal renders either kind: same layout,
// just different labels/rows and no retry action for deletions.
const ImportReportModal = ({ reportId, sources, siteName, fallbackStats, reportType = 'import', onClose, onRetryFailed, retrying = false, aispaceColor, t }) => {
  const dispatch = useDispatch()
  const { syncReportDetail, syncReportDetailLoading } = useSelector(state => state.sharepoint)

  useEffect(() => {
    if (reportId) dispatch(fetchSyncReportDetail(reportId))
  }, [reportId, dispatch])

  const report = reportId && syncReportDetail && syncReportDetail.id === reportId ? syncReportDetail : null
  const isDelete = (report?.report_type || reportType) === 'delete'
  const isStopped = report?.report_type === 'stopped'
  const isDiscarded = report?.report_type === 'discarded'
  const results = report?.results || []
  const skipped = results.filter(r => r.status === 'skipped')
  const failed = results.filter(r => r.status === 'error')
  // Successful entries — 'removed' for a deletion, 'synced' for an import —
  // shown as a hover tooltip on the count so "10 files" isn't just a
  // number with no way to see which files they actually were.
  const succeeded = results.filter(r => r.status === 'removed' || r.status === 'synced')
  const succeededTooltip = succeeded.length > 0 ? succeeded.map(r => r.file_name).join('\n') : undefined
  const loading = reportId && (!report || syncReportDetailLoading)

  const row = (label, value, tooltip) => (
    <div className="px-4 py-3 flex items-center justify-between gap-4">
      <p className="text-[13px] text-[#6B7280]">{label}</p>
      <p className="text-[14px] font-semibold text-[#111827] text-right" title={tooltip}>{value}</p>
    </div>
  )

  return (
    <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-[120]" onClick={onClose}>
      <div className="bg-white rounded-xl w-[560px] shadow-xl overflow-hidden flex flex-col max-h-[85vh]" onClick={(e) => e.stopPropagation()}>
        <div className="px-6 py-5 flex items-start justify-between">
          <div>
            <h2 className="text-[18px] font-bold text-[#111827]">
              {isDelete
                ? t('agentSettings.filesDeletionReportTitle', { siteName: report?.site_name || siteName || '' })
                : t('agentSettings.filesImportReportTitle', { siteName: report?.site_name || siteName || '' })}
            </h2>
            {report && (
              <p className="text-[13px] text-[#6B7280] mt-1">
                {t('agentSettings.filesImportReportStarted', { date: formatDateTime(report.started_at) })}
              </p>
            )}
          </div>
          <button onClick={onClose} className="p-1 hover:bg-[#F3F4F6] rounded-lg transition-colors flex-shrink-0">
            <X size={20} className="text-[#6B7280]" />
          </button>
        </div>

        <div className="px-6 pb-4 flex-1 overflow-y-auto">
          {loading ? (
            <div className="flex items-center justify-center py-16">
              <Loader2 size={24} className="animate-spin" style={{ color: aispaceColor }} />
            </div>
          ) : !report ? (
            <>
              {/* Fallback: no report row exists (either a manual one-by-one
                  import with no Sync All report, or the report save itself
                  failed) — no file-type/page-limit/duration/per-file detail
                  to show, just the same summary numbers already on the card. */}
              <div className="border border-[#E5E7EB] rounded-xl divide-y divide-[#E5E7EB] mb-2">
                {row(t('agentSettings.filesImportReportSources'), (sources && sources.length > 0) ? sources.join(', ') : '—')}
                {row(
                  isDelete ? t('agentSettings.filesImportReportRemoved') : t('agentSettings.filesImportReportImported'),
                  t('agentSettings.filesImportReportFileCount', { count: fallbackStats?.filesImported || 0 })
                )}
                {!isDelete && row(t('agentSettings.filesImportReportSkipped'), t('agentSettings.filesImportReportFileCount', { count: fallbackStats?.skipped || 0 }))}
                {row(t('agentSettings.filesImportReportFailed'), t('agentSettings.filesImportReportFileCount', { count: fallbackStats?.failed || 0 }))}
                {row(t('agentSettings.filesImportReportLastImport'), formatDateTime(fallbackStats?.lastImportAt))}
              </div>
              <p className="text-[13px] text-[#6B7280]">{t('agentSettings.filesImportReportNoDetailHint')}</p>
            </>
          ) : (
            <>
              {(isStopped || isDiscarded) && (
                <div className="mb-3 px-4 py-3 rounded-xl bg-[#FFFBEB] border border-[#FDE68A] flex items-start gap-2">
                  <AlertTriangle size={16} className="text-[#D97706] flex-shrink-0 mt-0.5" />
                  <p className="text-[13px] text-[#92400E]">
                    {isDiscarded
                      ? t('agentSettings.filesImportReportDiscardedNote')
                      : (fallbackStats?.filesImported != null
                          ? t('agentSettings.filesImportReportStoppedNoteWithTotal', { count: report.synced_count || 0, total: fallbackStats.filesImported })
                          : t('agentSettings.filesImportReportStoppedNote', { count: report.synced_count || 0 }))}
                  </p>
                </div>
              )}
              <div className="border border-[#E5E7EB] rounded-xl divide-y divide-[#E5E7EB] mb-2">
                {row(t('agentSettings.filesImportReportSources'), (sources && sources.length > 0) ? sources.join(', ') : '—')}
                {!isDelete && row(t('agentSettings.filesImportReportFileTypes'), formatFileTypes(report.file_types))}
                {!isDelete && row(t('agentSettings.filesImportReportPageLimit'), report.max_pages > 0 ? t('agentSettings.filesImportReportMaxPages', { max: report.max_pages }) : t('agentSettings.filesImportReportNoLimit'))}
                {row(
                  isDelete ? t('agentSettings.filesImportReportRemoved') : t('agentSettings.filesImportReportImported'),
                  t('agentSettings.filesImportReportFileCount', { count: report.synced_count || 0 }),
                  succeededTooltip
                )}
                {!isDelete && row(t('agentSettings.filesImportReportSkipped'), t('agentSettings.filesImportReportFileCount', { count: report.skipped_count || 0 }))}
                {row(t('agentSettings.filesImportReportFailed'), t('agentSettings.filesImportReportFileCount', { count: report.error_count || 0 }))}
                {row(t('agentSettings.filesImportReportDuration'), formatDuration(report.started_at, report.finished_at))}
              </div>

              {isDelete && succeeded.length > 0 && (
                <div className="mb-5">
                  <h3 className="text-[14px] font-semibold text-[#111827] mb-1">{t('agentSettings.filesDeletionReportRemovedTitle')}</h3>
                  <div className="border border-[#E5E7EB] rounded-xl divide-y divide-[#E5E7EB]">
                    {succeeded.map((r, i) => (
                      <div key={i} className="px-4 py-3 flex items-start gap-2">
                        <CheckCircle2 size={16} className="text-[#059669] flex-shrink-0 mt-0.5" />
                        <div className="min-w-0">
                          <p className="text-[14px] font-semibold text-[#111827] truncate">{r.file_name}</p>
                          {r.file_path && <p className="text-[13px] text-[#6B7280] truncate">{r.file_path}</p>}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {skipped.length > 0 && (
                <div className="mb-5">
                  <h3 className="text-[14px] font-semibold text-[#111827] mb-1">{t('agentSettings.filesImportReportSkippedTitle')}</h3>
                  <p className="text-[13px] text-[#6B7280] mb-2">{t('agentSettings.filesImportReportSkippedHint')}</p>
                  <div className="border border-[#E5E7EB] rounded-xl divide-y divide-[#E5E7EB]">
                    {skipped.map((r, i) => (
                      <div key={i} className="px-4 py-3 flex items-start gap-2">
                        <AlertTriangle size={16} className="text-[#D97706] flex-shrink-0 mt-0.5" />
                        <div className="min-w-0">
                          <p className="text-[14px] font-semibold text-[#111827] truncate">{r.file_name}</p>
                          <p className="text-[13px] text-[#6B7280]">{r.reason}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {failed.length > 0 && (
                <div>
                  <h3 className="text-[14px] font-semibold text-[#111827] mb-1">
                    {isDelete ? t('agentSettings.filesDeletionReportFailedTitle') : t('agentSettings.filesImportReportFailedTitle')}
                  </h3>
                  <p className="text-[13px] text-[#6B7280] mb-2">
                    {isDelete ? t('agentSettings.filesDeletionReportFailedHint') : t('agentSettings.filesImportReportFailedHint')}
                  </p>
                  <div className="border border-[#E5E7EB] rounded-xl divide-y divide-[#E5E7EB]">
                    {failed.map((r, i) => (
                      <div key={i} className="px-4 py-3 flex items-start gap-2">
                        <AlertTriangle size={16} className="text-[#DC2626] flex-shrink-0 mt-0.5" />
                        <div className="min-w-0">
                          <p className="text-[14px] font-semibold text-[#111827] truncate">{r.file_name}</p>
                          <p className="text-[13px] text-[#6B7280]">{formatEmbedError(r.error, t)}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        <div className="flex items-center justify-end gap-3 px-6 py-4 bg-[#F9FAFB] border-t border-[#E5E7EB]">
          <button
            onClick={onClose}
            className="px-5 py-2.5 text-[14px] font-medium text-[#374151] bg-white border border-[#D1D5DB] rounded-lg hover:bg-[#F9FAFB] transition-colors"
          >
            {t('common.close')}
          </button>
          {!isDelete && failed.length > 0 && onRetryFailed && (
            <button
              onClick={onRetryFailed}
              disabled={retrying}
              className="flex items-center justify-center gap-2 px-5 py-2.5 text-[14px] font-medium text-white rounded-lg hover:opacity-90 transition-colors disabled:opacity-50"
              style={{ backgroundColor: aispaceColor }}
            >
              {retrying && <Loader2 size={14} className="animate-spin" />}
              {t('agentSettings.filesRetryFailedFiles', { count: failed.length })}
            </button>
          )}
        </div>
      </div>
    </div>
  )
}

export default ImportReportModal
